import logoIcon from '../assets/images/standalone_logo.png';
import logoWordmark from '../assets/images/logo_aktivity.png';

// In-memory cache so repeat PDF exports in the same session don't re-fetch
// and re-encode an asset. Keyed by the webpack URL, so the icon and the
// wordmark cache independently; the in-flight promise is cached too, so
// concurrent callers share one fetch.
const cache = new Map();
const inFlight = new Map();

/**
 * Fetch an imported image asset and return it as a base64 PNG data URL,
 * suitable for `doc.addImage(data, 'PNG', x, y, w, h)` in jsPDF.
 *
 * Returns `null` on failure (network blocked, FileReader error, etc.) so
 * callers can fall back to a text-only header instead of throwing.
 */
const loadAsDataUrl = (src) => {
  if (cache.has(src)) return Promise.resolve(cache.get(src));
  if (inFlight.has(src)) return inFlight.get(src);

  const promise = (async () => {
    try {
      // `src` is the URL webpack assigned to the imported asset — a hashed
      // `/static/media/...png` path in production, a dev-server URL locally.
      // Fetching it gives us the raw bytes either way.
      const response = await fetch(src);
      if (!response.ok) return null;
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
      cache.set(src, dataUrl);
      return dataUrl;
    } catch (err) {
      console.error('Failed to load image for PDF:', err);
      return null;
    } finally {
      inFlight.delete(src);
    }
  })();

  inFlight.set(src, promise);
  return promise;
};

/**
 * The square Aktivity mark. Natural size 1024x1024, so draw it square:
 *   doc.addImage(data, 'PNG', 14, 6, 11, 11);
 */
export const loadLogoForPdf = () => loadAsDataUrl(logoIcon);

/**
 * The "Aktivity" wordmark, as used in the header of the server-rendered
 * reports. Natural size 929x341 (ratio 2.72), so keep that aspect:
 *   doc.addImage(data, 'PNG', 14, 10, 8 * 2.72, 8);
 */
export const loadWordmarkForPdf = () => loadAsDataUrl(logoWordmark);

/** Width-to-height ratio of the wordmark, so callers don't hardcode it. */
export const WORDMARK_RATIO = 929 / 341;

export default loadLogoForPdf;
